/**
 * Christmas doodles — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const christmasDoodlesPack: ToilePack = {
    slug: 'christmas-doodles',
    category: 'season',
    name: 'Christmas doodles',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'mixed', label: 'Mixed doodles', subject: 'A page packed with small christmas doodles: mittens, a mug, a candle, a bell, a star, a tree, a sock, no gaps'},
        {key: 'food', label: 'Festive food', subject: 'A page of small food doodles: gingerbread, a pudding, a candy cane, a mince pie, cookies, a cocoa mug, packed tight'},
        {key: 'winter', label: 'Winter doodles', subject: 'A page of winter doodles: snowflakes, a scarf, a sled, a snowman, a pine sprig, a lantern, packed tight'},
    ],
    styles: [
        {key: 'bold', label: 'Bold doodles', style: 'bold marker-doodle outline, thick confident uniform strokes', palette: 'Black ink only', framing: 'Packed edge to edge across the page with a thin white margin, no gaps between doodles'},
        {key: 'fine', label: 'Fine line doodles', style: 'fine pen doodle line, thin delicate uniform strokes', palette: 'Black ink only', framing: 'Neatly spaced across the page with a small white margin'},
        {key: 'bubble', label: 'Bubble doodles', style: 'chunky bubble-outline doodle line, thick rounded uniform strokes with soft corners', palette: 'Black ink only', framing: 'Arranged in a loose grid across the page with a white margin'},
    ],
    directions: ['a single large doodle', 'in a bullet-journal grid', 'as a sticker sheet'],
    directionHint: 'try: a single large doodle instead',
    seo: {
        title: 'Christmas Doodle Art: Mittens, Cocoa, Snowman',
        metaDescription: 'Nine christmas doodle sheets — mittens, cocoa, a snowman, a candle, a bell — packed edge to edge, easy hand-drawn style, three styles.',
        h1: 'Christmas doodle art: mittens, cocoa, snowman',
        tagline: 'Nine sheets, three December subjects, three styles each.',
        intro: 'Pages of small, hand-drawn-style doodles packed edge to edge: mittens, cocoa, a snowman, a candle, a bell, built loose and easy. Ask for a wreath, a candy cane, or your own subject, and the same three styles carry it.',
        useCases: [
            'a bullet journal spread',
            'a handmade holiday card',
            'a sticker sheet layout',
            'a scrapbook or planner page',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine doodle sheets — mittens, cocoa, a snowman, a candle, a bell, packed edge to edge — in three styles.'},
            {q: 'Are these easy enough to trace or copy by hand?', a: 'Yes — the style is deliberately simple and loose, closer to hand-drawn doodles than detailed illustration.'},
            {q: 'Can I cut individual doodles out for a sticker sheet?', a: 'Yes — each sheet packs several small doodles you can crop or trace individually.'},
            {q: 'Can I get a different subject, like a wreath or candy cane?', a: 'Yes — the direction field takes any Christmas subject, including a wreath or a candy cane.'},
            {q: "Can I use these in a product I'm selling, like printed stickers?", a: 'Yes — check the license terms on your account for the specifics of commercial use.'},
        ],
    },
}
